import React, { useState, useEffect, useRef } from "react";
import logo from "../images/logo.png";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { useAuth } from "../hooks/useAuth";

const Header = () => {
  const { user, isAdmin, isModerator, isUser } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await auth.signOut();
    navigate("/signin");
    setIsMobileMenuOpen(false);
    setIsUserDropdownOpen(false);
  };

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  const closeUserDropdown = () => {
    setIsUserDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="relative bg-gradient-to-r from-valia-primary via-valia-primary-light to-valia-primary shadow-valia-lg border-b border-valia-primary-dark">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-valia-secondary/10 via-transparent to-valia-accent/10"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-valia-secondary/20 via-transparent to-transparent"></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4 min-h-[72px]">
        <div className="flex items-center gap-4">
          <Link to="/" onClick={closeMobileMenu}>
            <img src={logo} alt="Valia Logo" className="h-10 w-auto" />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-2 text-sm font-medium">
          {user ? (
            <>
              <Link
                to="/dashboard"
                className="px-4 py-2.5 text-white hover:text-valia-accent hover:bg-white/10 rounded-valia transition-all duration-200 font-medium"
              >
                Dashboard
              </Link>
              <Link
                to="/agents"
                className="px-4 py-2.5 text-white hover:text-valia-accent hover:bg-white/10 rounded-valia transition-all duration-200 font-medium"
              >
                Agents
              </Link>
              {isAdmin && (
                <>
                  <Link
                    to="/users"
                    className="px-4 py-2.5 text-white hover:text-valia-accent hover:bg-white/10 rounded-valia transition-all duration-200 font-medium"
                  >
                    Users
                  </Link>
                </>
              )}
              {isAdmin && (
                <Link
                  to="/logs"
                  className="px-4 py-2.5 text-white hover:text-valia-accent hover:bg-white/10 rounded-valia transition-all duration-200 font-medium"
                >
                  Logs
                </Link>
              )}
              <div className="relative ml-2" ref={dropdownRef}>
                <button
                  onClick={toggleUserDropdown}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-valia transition-colors duration-200"
                >
                  <div className="w-5 h-5 bg-valia-accent rounded-full flex items-center justify-center text-sm font-medium text-white">
                    {(user?.displayName || user?.email || "U")
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                  <span className="text-white text-sm hidden lg:block font-medium">
                    {user?.displayName || user?.email || "User"}
                  </span>
                  <svg
                    className={`w-4 h-4 text-white transition-transform duration-200 ${
                      isUserDropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* User Dropdown Menu */}
                {isUserDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-valia-primary rounded-valia-lg shadow-valia-xl border border-valia-primary-light py-2 z-50 animate-slide-in-top">
                    <div className="px-4 py-3 border-b border-valia-primary-light">
                      <p className="text-sm font-semibold text-white mb-1">
                        {user?.displayName || "User"}
                      </p>
                      <p className="text-xs text-valia-gray-300 truncate">
                        {user?.email}
                      </p>
                    </div>
                    <button
                      onClick={handleSignOut}
                      className="w-48 mx-3 my-2 px-4 py-2 text-xs text-valia-gray-300 hover:text-white hover:bg-valia-primary-light border border-white rounded-valia transition-all duration-200 font-medium"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to="/register"
                className="px-4 py-2 text-white hover:text-valia-accent hover:bg-white/10 rounded-valia transition-all duration-200 font-medium"
              >
                Register
              </Link>
              <Link
                to="/signin"
                className="px-6 py-2 bg-valia-accent hover:bg-valia-accent-light text-white rounded-valia transition-all duration-200 font-medium shadow-sm hover:shadow-md"
              >
                Sign in
              </Link>
            </>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="md:hidden p-2 rounded-valia hover:bg-valia-gray-50 transition-all duration-300 border border-valia-gray-200"
          aria-label="Toggle mobile menu"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {isMobileMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-valia-gray-200 animate-slide-in-top">
          <nav className="px-4 py-4 space-y-3">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 rounded-valia hover:bg-valia-gray-50 hover:text-valia-primary transition-all duration-300 text-sm font-medium"
                >
                  Dashboard
                </Link>
                <Link
                  to="/agents"
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 rounded-valia hover:bg-valia-gray-50 hover:text-valia-primary transition-all duration-300 text-sm font-medium"
                >
                  Agents
                </Link>
                {isAdmin && (
                  <Link
                    to="/logs"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded-valia hover:bg-valia-gray-50 hover:text-valia-primary transition-all duration-300 text-sm font-medium"
                  >
                    Log List
                  </Link>
                )}
                {isAdmin && (
                  <>
                    <Link
                      to="/users"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-valia hover:bg-valia-gray-50 hover:text-valia-primary transition-all duration-300 text-sm font-medium"
                    >
                      Users
                    </Link>
                  </>
                )}
                <div className="px-3 py-2 text-valia-gray-600 text-sm border-t border-valia-gray-200 mt-2 pt-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-valia-primary rounded-full flex items-center justify-center text-xs font-medium text-white">
                      {(user?.displayName || user?.email || "U")
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                    <span>{user?.displayName || user?.email || "User"}</span>
                  </div>
                </div>
                <button
                  onClick={handleSignOut}
                  className="block w-full text-left px-3 py-2 rounded-valia hover:bg-valia-gray-50 hover:text-valia-error transition-all duration-300 text-sm font-medium bg-transparent border-none cursor-pointer"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/register"
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 rounded-valia hover:bg-valia-gray-50 hover:text-valia-accent transition-colors duration-200 text-sm font-medium"
                >
                  Register
                </Link>
                <Link
                  to="/signin"
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 rounded-valia hover:bg-valia-gray-50 hover:text-valia-accent transition-colors duration-200 text-sm font-medium"
                >
                  Sign in
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
